import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import useUserData, { UserType } from 'hooks/useUserData';
import Avatar from 'components/common/avatar/Avatar';
import { highlightSearchKeyword, linkToUser } from 'utility/helpers';
import FileListLineIcon from 'remixicon-react/FileListLineIcon';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import Dot from 'components/common/dot/Dot';
import {
  $BoxWrapper,
  $BoxTitle,
  $BoxSubTitle,
  $BoxInfo,
  $ImageBox,
} from 'components/common/stylings';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import useCompanyQueryData from 'hooks/useCompanyQueryData';
import { ReactComponent as SMEBadge } from 'assets/icons/sme-badge.svg';
import { useBuildWiseModules } from '@buildwise/libs/helpers';

type UserBoxPropTypes = {
  user: UserType;
  searchKeyword?: string;
};

const UserBox: React.FC<UserBoxPropTypes> = ({ user, searchKeyword }) => {
  const { shouldDisplayCompanyOnUserCards } = useCompanyQueryData();
  const { company } = useUserData();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const toUserURL = useMemo(
    () =>
      `${linkToUser(user.username)}${searchParams.get('q') ? `?q=${searchParams.get('q')}` : ''}`,
    [user.username, searchParams],
  );
  const { projectsModule, pluralProjectsModuleName } = useBuildWiseModules();

  return (
    <$BoxWrapper>
      <Box
        sx={{ display: 'flex', width: '100%', position: 'relative' }}
        component={Link}
        to={toUserURL}
        state={state}
      >
        <$ImageBox>
          <Avatar
            src={user?.profileImageURL}
            size={44}
            fontSize={24}
            name={user.userNameToDisplay}
          />
        </$ImageBox>
        <Box flexGrow={1}>
          <$BoxTitle
            dangerouslySetInnerHTML={{
              __html: highlightSearchKeyword(user?.userNameToDisplay, searchKeyword),
            }}
          />
          <Box>
            {user.title && (
              <Typography variant="caption" fontSize={14} title="Job Title">
                {user.title}
              </Typography>
            )}
            {shouldDisplayCompanyOnUserCards &&
              (user?.primaryCompanyName || user?.userPrimaryCompanyName) && (
                <>
                  {user.title && <Dot />}
                  <Typography variant="caption" fontSize={14} title="Company">
                    {user?.primaryCompanyName || user?.userPrimaryCompanyName}
                  </Typography>
                </>
              )}
          </Box>
          {(user.city || user.state_code || user.country_code) && (
            <$BoxSubTitle>
              {user.city}
              {user.city && (user.state_code || user.country_code) ? ',' : ''} {user.state_code}
              {user.state_code && user.country_code ? ',' : ''} {user.country_code}
            </$BoxSubTitle>
          )}
          {user.department_or_region && (
            <$BoxSubTitle>
              <b>Department: &nbsp;</b>
              {user.department_or_region}
            </$BoxSubTitle>
          )}
          {/* {!!user.mySkills?.length && (
            <$BoxSubTitle>
              <b>Expert Skills: &nbsp;</b>
              {user.mySkills.join(', ')}
            </$BoxSubTitle>
          )} */}

          <$BoxInfo>
            <FileListLineIcon size={18} />
            {user.userTotalArticles || 0} Posts
            {user.userTotalProjects >= 0 && !!projectsModule?.isActive && (
              <>
                <Dot />
                <BuildingLineIcon size={18} style={{ marginRight: 4 }} />
                {user.userTotalProjects || 0} {pluralProjectsModuleName}
              </>
            )}
          </$BoxInfo>
        </Box>
        {((user?.SMESkills && user?.SMESkills.length > 0) || user?.isSME) && (
          <Box
            sx={(theme) => ({
              color: theme.palette.custom.link,
              backgroundColor: theme.palette.custom.aliceBlue,
              display: 'flex',
              alignItems: 'center',
              borderRadius: '4px',
              padding: '2px 8px 2px 8px',
              position: 'absolute',
              right: 0,
              top: 0,
            })}
          >
            <SMEBadge style={{ width: 16, height: 16, marginRight: 2 }} />
            <Typography>SME</Typography>
          </Box>
        )}
      </Box>
    </$BoxWrapper>
  );
};

export default UserBox;
